import _ from "lodash";
import withAsyncRouteLoading from "UIComponents/AsyncRouteLoadingHoc";
import { injectReducer } from "store/reducers";

import {
  AttendanceSettingsV2,
  RolesAndPermissions,
  Transcripts,
  SchoolAnalytics,
  BulkUploadActions,
  AttendanceAndTimetableSettings,
  ActivityLogs,
  AcademicYearTransition,
} from "Administrator/routes";

import AccountInformation from "AdminSettings/routes/AccountInformation";
import GradeLevelsWrapper from "AdminSettings/routes/GradeLevelsWrapper";
import OrganizationalStructure from "AdminSettings/routes/OrganizationalStructure";
import SchoolInformation from "AdminSettings/routes/SchoolInformationWrapper";
import TeacherCoursesWrapper from "AdminSettings/routes/TeacherCoursesWrapper";
import BlueprintCoursesWrapper from "AdminSettings/routes/BlueprintCoursesWrapper";
import Classes from "AdminSettings/routes/Classes";
import AcademicYears from "AdminSettings/routes/AcademicYears";
import UserDirectory from "AdminSettings/routes/UserDirectory";
import YearGroups from "AdminSettings/routes/YearGroups";
import AllWorkflows from "AdminSettings/routes/AllWorkflows";
import YearGroupManagerWrapper from "AdminSettings/routes/YearGroupManagerWrapper";
import {
  SchoolOperationsRoute,
  IntegrationsRoute,
  StudentTransfersRoute,
} from "AppComponents/AdminNavigation";

import BehaviourConfiguration from "SchoolSetup/routes/BehaviourConfiguration";
import Incident from "Incident";
import ModuleOnOff from "Administrator/routes/SchoolSettings/routes/ModuleSettings";
import ModuleSettingsV2 from "Administrator/routes/SchoolSettings/routes/ModuleSettingsV2";
import CourseAndClassModuleSettings from "Administrator/routes/SchoolSettings/routes/CourseAndClassModuleSettings/entries/account";
import GlobalModuleSettings from "Administrator/routes/SchoolSettings/routes/GlobalModuleSettings/entries/account";
import UserNameSettings from "Administrator/routes/SchoolSettings/routes/UserNameSettings";
import StudentAlertsSettings from "StudentAlertsSettings";
import Turnitin from "Administrator/routes/Settings/routes/TurnItInSettings";
import { OperationalDaysAndTimeZone } from "SchoolSetup/routes";
import Students from "SchoolSetup/routes/Students";
import AcademicYearTerms from "SchoolSetup/routes/AcademicYearTerms";
import TeacherSetup from "SchoolSetup/routes/TeacherSetup";
import ExternalAssessmentSetup from "ExternalAssessmentSetup";
import {
  ExternalAssessmentRecordsRoute,
  ExternalAssessmentTemplatesRoute,
} from "ExternalAssessments";
import ManageFamily from "Course/routes/ManageFamily";
import SchoolInformationSetup from "SchoolSetup/routes/SchoolInformation";
import UnitPlanPdfDownloadTool from "SchoolSetup/routes/UnitPlanPdfDownloadTool";
import Integrations from "SchoolSetup/routes/Integrations";
import AssessmentTransfer from "SchoolSetup/routes/AssessmentTransfer";
import YearGroupsV1 from "SchoolSetup/routes/YearGroups";
import CourseSetup from "SchoolSetup/routes/CourseSetup";
import GradesSetup from "SchoolSetup/routes/GradesSetup";
import Sync from "Administrator/routes/SchoolSetup/routes/Sync";
import SyncV2 from "SchoolSetup/routes/SyncV2";
import LoginConfigurationManager from "SchoolSetup/routes/LoginConfigurationManager";

import CourseCodesWrapper from "AdminSettings/routes/CourseCodesWrapper";
import CurriculumProgram from "AdminSettings/routes/CurriculumProgram";
import AcademicCurriculumProgram from "./routes/AcademicCurriculumProgram";
import AssessmentToolTransfterWrapper from "AdminSettings/routes/AssessmentToolTransfterWrapper";
import { clearAdminNavigationState } from "Utils/AdminNavigationUtils/AdminNavigationModule";
import GradingPeriodWrapper from "./routes/GradingPeriodWrapper";
import AccountGradingPeriods from "./routes/AccountGradingPeriods";
import SchoolWiseSetup from "./routes/SchoolWiseSetup";
import AcademicYearsV2 from "./routes/AcademicYearsV2";
import ExternalStaff from "./routes/ExternalStaff";
import SFTP from "SchoolSetup/routes/SFTP";
import AcademyAnalytics from "Administrator/routes/AcademyAnalytics";
import TranscriptsV2 from "./routes/TranscriptsV2";
import SchoolisationSettings from "./routes/SchoolisationSettings";
import TranscriptsV3 from "./routes/TranscriptsV3";
import Departments from "./routes/Departments";
import Bin from "./routes/Bin";
import AcademicYearTransitionV2 from "./routes/AcademicYearTransition";
import CalendarSettings from "./routes/CalendarSettings";
import AccountSchoolPolicies from "Administrator/routes/AdminEducatorCenterV2/entries/account";
import AdmissionPocPlaceholders from "./routes/AdmissionPocPlaceholders";
import {
  ADMIN_PORTAL_POC_VARIANT,
  ADMIN_PORTAL_POC,
} from "Constants/adminPortalPocVariant";

export default store => ({
  path: "accountSettings",
  component: withAsyncRouteLoading(() =>
    Promise.all([
      import(
        /* webpackChunkName: "admin-settings" */ "./components/AdminSettings"
      ),
      import(
        /* webpackChunkName: "administrator" */ "Administrator/modules/AdministratorModule"
      ),
      import(
        /* webpackChunkName: "admin-schoolSetup" */ "SchoolSetup/modules/SchoolSetupModule"
      ),
      import(
        /* webpackChunkName: "admin-schoolSetup" */ "SchoolSetup/modules/StudentEnrollModalModule"
      ),
      import(
        /* webpackChunkName: "admin-schoolSetup" */ "ResetInviteAndSignInCodeModal/modules/ResetInviteAndSignInCodeModalModule"
      ),
      import(
        /* webpackChunkName: "admin-schoolSetup" */ "SchoolSetup/modules/StaffEnrollModalModule"
      ),
      import(
        /* webpackChunkName: "admin-navigation" */ "Utils/AdminNavigationUtils/AdminNavigationModule"
      ),
    ]).then(
      ([
        importedModule0,
        importedModule1,
        importedModule2,
        importedModule3,
        importedModule4,
        importedModule5,
        importedModule6,
      ]) => {
        const component = importedModule0.default;
        const reducer = importedModule1.default;
        const SchoolSetupModule = importedModule2.default;
        const studentEnrollModalModule = importedModule3.default;
        const resetInviteAndSignInCodeModalModule = importedModule4.default;
        const staffEnrollModalModule = importedModule5.default;

        injectReducer(store, {
          key: "administrator",
          reducer,
        });
        injectReducer(store, {
          key: "schoolSetup",
          reducer: SchoolSetupModule,
        });
        injectReducer(store, {
          key: "studentEnrollModal",
          reducer: studentEnrollModalModule,
        });
        injectReducer(store, {
          key: "resetInviteAndSignInCodeModal",
          reducer: resetInviteAndSignInCodeModalModule,
        });
        injectReducer(store, {
          key: "staffEnrollModal",
          reducer: staffEnrollModalModule,
        });
        injectReducer(store, {
          key: "adminNavigation",
          reducer: importedModule6.default,
        });

        return component;
      }
    )
  ),
  childRoutes: _.map([
    AcademicYearTransition(store),
    AccountInformation(store),
    OrganizationalStructure(store),
    SchoolInformationSetup(store),
    SchoolInformation(store),
    OperationalDaysAndTimeZone(store),
    AcademicYears(store),
    RolesAndPermissions(store),
    AttendanceSettingsV2(store),
    GradeLevelsWrapper(store),
    CourseCodesWrapper(store),
    StudentTransfersRoute(store),
    TeacherCoursesWrapper(store),
    BlueprintCoursesWrapper(store),
    Classes(store),
    UserDirectory(store),
    YearGroups(store),
    AllWorkflows(store),
    BehaviourConfiguration(store),
    Incident(store),
    ModuleOnOff(store),
    ModuleSettingsV2(store),
    CourseAndClassModuleSettings(store),
    GlobalModuleSettings(store),
    UserNameSettings(store),
    StudentAlertsSettings(store),
    Turnitin(store),
    YearGroupManagerWrapper(store),
    Transcripts(store),
    Students(store),
    ManageFamily(store),
    TeacherSetup(store),
    AcademicYearTerms(store),
    UnitPlanPdfDownloadTool(store),
    AttendanceAndTimetableSettings(store),
    SchoolAnalytics(store),
    Integrations(store),
    AssessmentTransfer(store),
    BulkUploadActions(store),
    YearGroupsV1(store),
    YearGroupManagerWrapper(store),
    GradesSetup(store),
    CourseSetup(store),
    ExternalStaff(store),
    Sync(store),
    SyncV2(store),
    IntegrationsRoute(store),
    AssessmentToolTransfterWrapper(store),
    SchoolOperationsRoute(store),
    GradingPeriodWrapper(store),
    AccountGradingPeriods(store),
    SchoolWiseSetup(store),
    AcademicYearsV2(store),
    ActivityLogs(store),
    LoginConfigurationManager(store),
    AcademyAnalytics(store),
    SFTP(store),
    TranscriptsV2(store),
    SchoolisationSettings(store),
    TranscriptsV3(store),
    CalendarSettings(store),
    Departments(store),
    AccountSchoolPolicies(store),
    AcademicYearTransitionV2(store),
    AcademicCurriculumProgram(store),
    Bin(store),
    ExternalAssessmentSetup(store),
    ExternalAssessmentTemplatesRoute(store),
    ExternalAssessmentRecordsRoute(store, { isAccountLevel: true }),
    CurriculumProgram(store),
    // POC 2 only — placeholder routes for the unified-portal Admission section.
    ...(ADMIN_PORTAL_POC_VARIANT === ADMIN_PORTAL_POC.UNIFIED_PORTAL
      ? [AdmissionPocPlaceholders(store)]
      : []),
  ]),
  onLeave: () => {
    /**
     * This is required to ensure that new route does not
     * access old redux state setup by current route during 1st render.
     */
    store.dispatch(clearAdminNavigationState());
  },
});
